// Tela de login (rota /auth) — autentica via Better Auth. Em sucesso, vai para /.
// Layout público alinhado ao MeetCore, com apresentação do Disk no desktop.
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { authClient, getAppAccess, invalidateAppAccess } from "@/lib/auth-client";
import { track } from "@/lib/analytics";
import { authErrorMessage } from "@/lib/auth-errors";
import {
  Button,
  Field,
  FieldContent,
  FieldGroup,
  FieldLabel,
  Input,
  Switch,
} from "@organizacaox/lai-design-system";
import { AuthLayout } from "./components/auth-layout";

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    try {
      // Better Auth pode devolver { error } (resposta do back) OU lançar (ex.: back inacessível).
      // rememberMe: quando false, a sessão expira ao fechar o navegador; true mantém logado.
      // `callbackURL`: quando o email ainda não foi confirmado, o back recusa o
      // login E manda um link novo (emailVerification.sendOnSignIn) — é esta
      // URL que o link vai abrir depois de confirmar. Para o login que dá
      // certo ela não muda nada: email+senha responde com a sessão, não com um
      // redirect.
      const { error: authError } = await authClient.signIn.email({
        email,
        password,
        rememberMe,
        callbackURL: `${window.location.origin}/`,
      });
      if (authError) {
        track("form_submission_error", { form: "login", error_type: "auth_rejected" });
        if (authError.code === "EMAIL_NOT_VERIFIED") {
          // Mensagem própria em vez da genérica do authErrorMessage: o fato
          // útil aqui é que um link NOVO acabou de sair — sem isso o usuário
          // fica procurando um email antigo que pode ter expirado.
          toast.error(t("auth.verifyEmailToSignIn"));
          return;
        }
        // Traduz o erro do Better Auth (ex.: "Invalid email or password") para PT-BR.
        toast.error(authErrorMessage(authError, t("auth.invalidCredentials")));
        return;
      }
      // Prepara sessão, marca ativa e papel antes de entrar. O guard da rota
      // reaproveita este snapshot e não repete as consultas do login.
      invalidateAppAccess();
      await getAppAccess();
      void navigate({ to: "/" });
    } catch {
      track("form_submission_error", { form: "login", error_type: "network" });
      toast.error(t("auth.serverUnavailable"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="flex justify-center items-center gap-2">
        <img src="/logo.svg" alt="Disk" className="w-20 h-20" />
        <h1 className="text-2xl font-bold tracking-tight">Disk</h1>
      </div>

      <p className="mb-6 text-sm text-center text-muted-foreground">{t("common.productTagline")}</p>

      <form onSubmit={onSubmit}>
        <FieldGroup className="gap-4">
          <Field>
            <FieldLabel htmlFor="email">{t("common.email")}</FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder={t("auth.emailPlaceholder")}
              autoComplete="username"
              autoCapitalize="none"
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="senha">{t("common.password")}</FieldLabel>
            <div className="relative">
              <Input
                id="senha"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
                required
              />
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t("common.hidePassword") : t("common.showPassword")}
                aria-pressed={showPassword}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
          </Field>

          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel htmlFor="remember-session">{t("auth.keepSignedIn")}</FieldLabel>
            </FieldContent>
            <Switch id="remember-session" checked={rememberMe} onCheckedChange={setRememberMe} />
          </Field>

          <Button
            type="submit"
            className="w-full"
            loading={loading}
            loadingLabel={t("auth.signingIn")}
          >
            {t("common.signIn")}
          </Button>

          <Button
            variant="link"
            className="w-full"
            nativeButton={false}
            role="link"
            render={<Link to="/forgot-password" />}
          >
            {t("auth.forgotPassword")}
          </Button>
        </FieldGroup>
      </form>

      <p className="mt-1 text-center text-sm text-muted-foreground">
        {t("auth.noAccount")}{" "}
        <Button
          variant="link"
          className="h-auto p-0"
          nativeButton={false}
          role="link"
          render={<Link to="/signup" />}
        >
          {t("auth.createAccount")}
        </Button>
      </p>

      <p className="mt-5 text-center text-xs leading-5 text-muted-foreground">
        {t("auth.legalPrefix")}{" "}
        <Link to="/terms" className="underline transition-colors hover:text-foreground">
          {t("auth.terms")}
        </Link>{" "}
        {t("auth.legalAnd")}{" "}
        <Link to="/privacy" className="underline transition-colors hover:text-foreground">
          {t("auth.privacy")}
        </Link>
        .
      </p>
    </AuthLayout>
  );
}
